// @ts-nocheck
import { capitalize } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  BoxV2,
  ButtonUI,
  CustomThemeProvider,
  Loader,
  Selector,
  SelectorOptionPropsInterface,
  usePortal,
  useQueryFetch,
  VeilV2
} from 'sonata-react-components';
import { type CountryInfo, countriesDictionary } from 'sonata-static-storage/lib/samples/countries.samples';

import {
  getCustomEvents,
  getInsightsConfig,
  getMetricsAndKpisByType,
  getReportingStatsGroups
} from '@/store/selectors/reporting.selectors';
import { getCampaigns, getTimeFrame, getTimeZone } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { AIInsightsMetricKpisSelector } from '@/ts-modules/reporting/AIInsightsModal/AIInsightsMetricKpisSelector';
import { InsightsItemsSelector } from '@/ts-modules/reporting/InsightsItemsSelector';
import { prepareTableHeaders } from '@/ts-modules/reporting/reportingExport.helpers';
import { getAIInsightsFromStats } from '@/ts-resources/reporting.resources';
import { AIInsightsModalStyled } from './AIInsightsModal.styled';

import type { GeoDictionary } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';
import type { ItemsByFamily } from '@/ts-modules/reporting/InsightsItemsSelector/InsightsItemsSelector';
import type { Columns } from '@/ts-modules/reporting/ReportDownload/types';

const DEFAULT_METRICS = [
  'impressions',
  'playouts',
  'bidMultiplier',
  'clicks',
  'ctr',
  'ecpe',
  'ecper',
  'ecpm',
  'ecppv',
  'ecpvr',
  'ecpv',
  'ecpal',
  'ecpc',
  'ctr',
  'vtr',
  'etr',
  'pages_viewed',
  'engagements',
  'atr',
  'media_fee',
  'total_spend',
  'total_viewers',
  'unique_users',
  'hvtr',
  'hctr',
  'true_ecpc',
  'true_ecpv',
  'true_hctr',
  'true_hvtr'
];

type Props = {
  campaigns: CampaignItem[];
  cartography: Partial<GeoDictionary>[];
  id: string;
  isOpen: boolean;
  onClose: () => void;
  onInsightsDataReady: (dataReadyState: boolean) => void;
  onInsightsLoading: (loadingState: boolean) => void;
  showGTotal?: boolean;
  stats: Record<string, any>;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

type State = {
  availableDimensionFamiliesOptions: {
    label: string;
    value: string;
  }[];
  availableDimensionsByFamily: ItemsByFamily;
  availableMetrics: { label: string; value: string }[];
  selectedDimensionsByFamily: ItemsByFamily;
  selectedLanguage: string;
  selectedMetrics: string[];
};

const convertMarkdownsToHtmlCharacters = (text: string): string => {
  // Use a regular expression to find patterns like **text**
  return text
    .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
    .replace(/^####\s*(.+)/gm, '<h3>$1</h3>')
    .replace(/^###\s*(.+)/gm, '<h2>$1</h2>');
};

function AIInsightsModal({
  campaigns,
  cartography,
  id,
  isOpen,
  onClose,
  onInsightsDataReady,
  onInsightsLoading,
  showGTotal,
  stats,
  user,
  userRoles
}: Props) {
  const { formatMessage } = useIntl();
  const targetBody = usePortal('aiInsightsModalPortal');
  const intl = useIntl();
  const modalRef: any = useRef();
  const aiInsightsStatsToken: Record<string, any> = {};
  const { config: insightsConfigs, filters: insightsFilters } = useSelector(getInsightsConfig);
  const customEvents = useSelector(getCustomEvents);
  const statsGroups = useSelector(getReportingStatsGroups);
  const metricsKpiByType = useSelector(getMetricsAndKpisByType);
  const cids: string[] = useSelector(getCampaigns);
  const timeFrame = useSelector(getTimeFrame);
  const timezone = useSelector(getTimeZone);

  const getDefaultFamilyItems = (family: string): string[] => {
    return insightsConfigs.find(({ type }) => type === family)!.items.map(({ id: itemId }) => itemId);
  };

  const getAvailableHeaders = (): Columns[] => {
    return prepareTableHeaders({
      campaigns,
      cartography,
      customEvents,
      intl,
      metricsAndKpis: metricsKpiByType,
      showGTotal,
      stats,
      statsGroups: statsGroups.stats,
      statsType: 'stats',
      user,
      userRoles
    });
  };

  const [
    {
      availableDimensionFamiliesOptions,
      availableDimensionsByFamily,
      availableMetrics,
      selectedDimensionsByFamily,
      selectedLanguage,
      selectedMetrics
    },
    setState
  ] = useState<State>({
    availableDimensionFamiliesOptions: insightsFilters
      .map(insightsFilter => ({
        ...insightsFilter,
        label: formatMessage({
          defaultMessage: capitalize(insightsFilter.value),
          id: insightsFilter.label
        })
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    availableDimensionsByFamily: insightsConfigs.reduce(
      (acc, { items: itemsTags, type }) => ({
        ...acc,
        ...(itemsTags.length > 0 && { [type]: itemsTags.map(({ id: itemTagId }) => itemTagId) })
      }),
      {}
    ),
    availableMetrics: getAvailableHeaders()
      .filter(({ filterType, prop }) => prop !== 'sdate' && !filterType)
      .map(({ cpy, prop }) => ({
        label: cpy,
        value: prop
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    selectedDimensionsByFamily: insightsConfigs.reduce(
      (acc, { items: itemsTags, type }) => ({
        ...acc,
        ...(type === 'audience' && itemsTags.length > 0 && { [type]: itemsTags.map(({ id: itemTagId }) => itemTagId) })
      }),
      {}
    ),
    selectedLanguage: 'English',
    selectedMetrics: getAvailableHeaders()
      .filter(({ filterType, prop }) => prop !== 'sdate' && !filterType && DEFAULT_METRICS.includes(prop))
      .map(({ prop }) => prop)
  });

  const selectedFamilyItems = Object.entries(selectedDimensionsByFamily)
    .filter(([, dimensions]) => dimensions.length)
    .map(([family]) => family);

  useEffect(() => {
    isOpen ? modalRef?.current?.openModal() : modalRef?.current?.closeModal();
  }, [isOpen]);

  const handleDimensionsChanged = (newDimensions: string[], isAllSelected?: boolean) => {
    const newDimensionFamilies = newDimensions.reduce(
      (acc, dimensionFamily) => ({
        ...acc,
        [dimensionFamily]: !isAllSelected
          ? availableDimensionsByFamily[dimensionFamily]
          : getDefaultFamilyItems(dimensionFamily)
      }),
      {}
    );
    setState(prevState => ({
      ...prevState,
      selectedDimensions: newDimensions,
      selectedDimensionsByFamily: newDimensionFamilies
    }));
  };

  const handleMetricsSelectedChanged = (newSelectedMetrics: string[]) => {
    setState(prevState => ({
      ...prevState,
      selectedMetrics: newSelectedMetrics
    }));
  };

  const languageOptions = Array.from(
    new Set(Object.values(countriesDictionary).map((item: CountryInfo) => item.language))
  )
    .map(language => ({
      id: language,
      label: language
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleLanguageSelectorChange = (option: SelectorOptionPropsInterface) => {
    setState(prevState => ({
      ...prevState,
      selectedLanguage: option.id as string
    }));
  };

  const {
    data: aiResponse,
    isFetching,
    isLoading,
    refetch,
    status
  } = useQueryFetch<string>(
    ['reporting', 'aiInsights', cids],
    async () => {
      onInsightsLoading(true);
      onInsightsDataReady(false);
      const formattedDimensions = Object.entries(selectedDimensionsByFamily).reduce((acc, [, dimensions]) => {
        const groupDimensions: string[] = [];
        insightsConfigs.forEach(({ items }) => {
          items.forEach(({ field, id: itemId }) => {
            if (dimensions.includes(itemId)) {
              if (field.includes('cartoPyramid')) {
                groupDimensions.push('cartoPyramid.postalCode');
              } else {
                groupDimensions.push(field);
              }
            }
          });
        });
        return acc.concat(groupDimensions);
      }, [] as string[]);
      const result = await getAIInsightsFromStats(
        cids,
        selectedMetrics,
        formattedDimensions,
        selectedLanguage,
        {
          end: timeFrame.endDate,
          start: timeFrame.beginDate,
          timezone
        },
        aiInsightsStatsToken
      );
      return convertMarkdownsToHtmlCharacters(result);
    },
    {
      enabled: false,
      onError: () => {
        onInsightsLoading(false);
        onInsightsDataReady(false);
      },
      onSuccess: () => {
        onInsightsLoading(false);
        onInsightsDataReady(true);
      }
    }
  );
  const aiDataIsLoading = status !== 'error' && (isLoading || isFetching);

  const handleAIGenerationToggled = () => {
    refetch();
    onClose();
  };

  const defaultLanguage = languageOptions.filter(option => option.id === selectedLanguage);

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <AIInsightsModalStyled
        className="aiInsightsModal"
        closeModalCallback={onClose}
        description={formatMessage({
          defaultMessage: 'Get AI-powered insights to optimize your campaigns.',
          id: 'reporting.aiInsights.description'
        })}
        id={id}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Insights Smart Analysis',
          id: 'reporting.aiInsights.title'
        })}
        width="1000px"
      >
        <section className="aiInsightsModal__configuration">
          {aiDataIsLoading && (
            <VeilV2 className="aiInsightsModal__loadingVeil" id="aiInsights-aiVeil">
              <Loader
                className="aiInsightsModal__loadingVeilSpinner"
                id="aiInsights-aiSpinner"
                type="landing"
                variant="default"
              >
                <Loader.Title className="aiInsightsModal__loadingVeilSpinnerTitle">
                  {formatMessage({
                    defaultMessage: 'Good things take time! Thins process will take a few minutes...',
                    id: 'reporting.loader.text'
                  })}
                </Loader.Title>
              </Loader>
            </VeilV2>
          )}
          <BoxV2 className="aiInsightsModal__languageSelector" id="reportingAIInsightsDimensions-config-box">
            <BoxV2.Body
              bgColor="aiInsightsModalBackgroundColor"
              borderRadius="8px"
              className="aiInsightsModal__languageSelectorBox"
              padding="10px 16px"
            >
              <h4 className="aiInsightsModal__languageSelectorBoxTitle">
                {formatMessage({
                  defaultMessage: 'Choose the language for the generated report',
                  id: 'reporting.aiInsights.languageSelector'
                })}
              </h4>
              <Selector
                className="aiInsightsModal__selector"
                defaultValue={defaultLanguage}
                onChange={handleLanguageSelectorChange}
                options={languageOptions}
                type="simple"
              />
            </BoxV2.Body>
          </BoxV2>
          <BoxV2 className="aiInsightsModal__dimensionSelector" id="reportingAIInsightsDimensions-config-box">
            <BoxV2.Body
              bgColor="aiInsightsModalBackgroundColor"
              borderRadius="8px"
              className="aiInsightsModal__dimensionSelectorBox"
              padding="10px 16px"
            >
              <h4 className="aiInsightsModal__dimensionSelectorBoxTitle">
                {formatMessage({
                  defaultMessage: 'Choose the data dimensions for your analysis',
                  id: 'reporting.aiInsights.dimensionsBox.title'
                })}
              </h4>
              <InsightsItemsSelector
                className="aiInsightsModal__dimensionSelectorTagSelector"
                id="aiInsightsModalDimensionSelector"
                items={availableDimensionFamiliesOptions}
                onChangeToggled={handleDimensionsChanged}
                selectedFamilyItems={selectedFamilyItems}
              />
            </BoxV2.Body>
          </BoxV2>
          <BoxV2
            className="aiInsightsModal__aggregationGroupsSelector"
            id="reportingAIInsightsAggregationGroups-config-box"
          >
            <BoxV2.Body
              bgColor="aiInsightsModalBackgroundColor"
              borderRadius="8px"
              className="aiInsightsModal_aggregationGroupsSelectorBox"
              padding="10px 16px"
            >
              <h4 className="aiInsightsModal__metricSelectorBoxTitle">
                {formatMessage({
                  defaultMessage: 'Choose the mentric and KPIs that will be used fot the analysis',
                  id: 'reporting.aiInsights.metricsBox.title'
                })}
              </h4>
              <AIInsightsMetricKpisSelector
                availableMetrics={availableMetrics}
                className="aiInsightsModal__metricSelectorTagSelector"
                defaultSelectedMetrics={selectedMetrics}
                onMetricSelectedChanged={handleMetricsSelectedChanged}
              />
            </BoxV2.Body>
          </BoxV2>
        </section>
        <section className="aiInsightsModal__submit">
          <CustomThemeProvider themeContrast="darkAI">
            <ButtonUI
              className="aiInsightsModal__generationButton"
              icon="fas fa-plus"
              loading={aiDataIsLoading}
              onClick={handleAIGenerationToggled}
              shape="fullRounded"
              text={formatMessage({
                defaultMessage: 'Generate Smart Analysis',
                id: 'reporting.aiInsights.button'
              })}
              type="primary"
            />
          </CustomThemeProvider>
        </section>
        {!aiDataIsLoading && !!aiResponse && (
          <BoxV2 borderColor="tableBorderRow" className="aiInsightsModal__details" id="reportingAIInsightsDetails">
            <BoxV2.Body className="aiInsightsModal__aiInsightsModal__detailsBox" padding="12px">
              <section className="aiInsightsModal__detailsAiResponse">
                <p dangerouslySetInnerHTML={{ __html: aiResponse }} />
              </section>
            </BoxV2.Body>
          </BoxV2>
        )}
      </AIInsightsModalStyled>
    </CustomThemeProvider>,
    targetBody
  );
}

export default AIInsightsModal;
